<template>
  <Card class="overflow-hidden shadow">
    <div
      class="bg-vectric border-b border-vectric py-5 sm:px-6 flex justify-between bg-white flex "
    >
      <h3
        class="bg-vectric text-black text-2xl leading-6 font-large tracking-wider pl-2 sm:pl-0 font-semibold"
      >
        Chat
      </h3>
      <span
        class="ml-6 h-7 flex items-center cursor-pointer"
        @click="() => (expandChat = !expandChat)"
      >
        <svg
          class="h-6 w-6 transform text-black"
          :class="{ 'rotate-0': !expandChat, '-rotate-180': expandChat }"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </span>
    </div>
    <div v-show="expandChat">
      <div class="relative">
        <DynamicScroller
          :items="messages"
          :min-item-size="54"
          class="scroller wrapper-chat"
          ref="messageChat"
          @scroll="handleScroll"
        >
          <template v-slot="{ item, index, active }">
            <DynamicScrollerItem
              :item="item"
              :active="active"
              :size-dependencies="[item.message, item.author, item.reply]"
              :data-index="index"
              v-bind:class="{ 'border-t border-gray-200': index > 0 }"
            >
              <message
                :message="item"
                v-on:remove-message="openModal"
                v-on:reply-to="handleReplyTo"
                v-on:scroll-to-message="handleScrollToMessage"
              ></message>
            </DynamicScrollerItem>
          </template>
        </DynamicScroller>
        <div
          v-if="showScroller"
          @click="handleScrollToBottom"
          style="position: absolute; bottom: 0; left:45%"
          class="pb-5"
        >
          <svg
            class="arrow-circle-down h-15 w-15 text-red
            transition duration-500 ease-in-out
            transform hover:-translate-y-1 hover:scale-110
            hover:text-red cursor-pointer"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </div>
      </div>
      <div class="relative my-5 mx-2 border-t " v-if="allowChat">
        <if-can-take-part login-class="border border-darkBlue mt-2">
          <ChatReplyNotice
            :replying-to="replyingTo"
            v-on:cancel-reply="handleCancelReply"
          />
          <message-input
            v-on:message-sent="onMessageInput"
            ref="messageInput"
          ></message-input>
        </if-can-take-part>
      </div>
    </div>

    <confirmation-modal
      v-if="modal"
      :message="toDelete"
      @confirm="handleRemoveMessage"
      @cancel="closeModal"
    />
  </Card>
</template>

<script>
import MessageInput from "./MessageInput";

import { auth, firebase, liveSessionChatCollection } from "../utils/firebase";
import Message from "./Message";
import { DynamicScroller } from "vue-virtual-scroller";
import { DynamicScrollerItem } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { mapGetters } from "vuex";
import { removeLiveChatMessage } from "../utils/functions";
import IfCanTakePart from "./IfCanTakePart";
import ConfirmationModal from "./ConfirmationModal";
import ChatReplyNotice from "./chat/ChatReplyNotice";
import { truncate } from "../utils/truncate";
import { debounce } from "../utils/debounce";
import Card from "./shared/Card.vue";

export default {
  name: "ChatBox",
  components: {
    Card,
    ChatReplyNotice,
    IfCanTakePart,
    MessageInput,
    Message,
    DynamicScroller,
    DynamicScrollerItem,
    ConfirmationModal
  },
  props: {
    allowChat: {
      type: Boolean,
      required: false,
      default: true
    },
    session: {
      type: Object,
      required: true
    },
    adminChat: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      replyingTo: null,
      liveChatSession: null,
      messages: [],
      id: null,
      messagesUnsubscribe: null,
      showChat: true,
      expandChat: true,
      scrollAtBottom: true,
      // This flag indicates a message is just in and we are waiting
      // before we update the scroll
      waiting: false,
      modal: false,
      toDelete: null
    };
  },
  mounted() {
    console.log("Adding event listener");
    const debouncedHandle = debounce(this.handleScroll, 200, false);
    this.$refs.messageChat.$el.addEventListener("scroll", debouncedHandle);
    this.fetchMessages();
  },
  methods: {
    handleReplyTo(message) {
      this.replyingTo = message;
      if (this.$refs.messageInput) {
        this.$refs.messageInput.focusInput();
      }
    },
    handleScrollToMessage(message) {
      const index = this.messages.findIndex(x => x.id === message.id);
      if (index != -1) {
        this.$refs.messageChat.scrollToItem(index);
      }
    },
    handleCancelReply() {
      this.replyingTo = null;
    },
    handleRemoveMessage() {
      this.$store.dispatch("setDeletedMessageId", this.toDelete.id);
      removeLiveChatMessage(this.session._id, this.toDelete.id);
      this.modal = false;
    },
    openModal(message) {
      this.toDelete = message;
      this.modal = true;
    },
    closeModal() {
      this.modal = false;
    },
    handleScroll() {
      if (!this.waiting) {
        const el = this.$refs.messageChat.$el;
        const scrollTop = el.scrollTop;
        const clientHeight = el.clientHeight;
        const scrollHeight = el.scrollHeight;
        this.scrollAtBottom =
          Math.abs(scrollHeight - scrollTop - clientHeight) < 5;
      }
    },
    handleScrollToBottom() {
      this.scrollToBottom();
      if (this.$refs.messageInput) {
        this.$refs.messageInput.focusInput();
      }
    },
    scrollToBottom() {
      console.info("From scrollToBottom function");
      this.$refs.messageChat.scrollToBottom();
    },
    fetchMessages() {
      this.id = this.session._id;

      console.log("Fetching message for this live session");
      const vm = this;
      vm.messages = [];
      this.messagesUnsubscribe = liveSessionChatCollection
        .doc(this.id)
        .collection("messages")
        .orderBy("timestamp")
        .onSnapshot(querySnapshot => {
          querySnapshot.docChanges().forEach(change => {
            const doc = change.doc;
            console.log(`Change type: ${change.type}`);

            if (change.type === "removed") {
              const index = this.messages.findIndex(item => item.id === doc.id);
              this.messages.splice(index, 1);
            }

            if (change.type === "added") {
              vm.messages.push({ id: doc.id, ...doc.data() });
            }

            if (change.type === "added") {
              if (vm.scrollAtBottom) {
                vm.waiting = true;
                vm.$nextTick(() => {
                  vm.scrollToBottom();
                  vm.waiting = false;
                });
              }
            }
          });
        });
    },

    beforeDestroy() {
      if (this.messagesUnsubscribe) {
        this.messagesUnsubscribe();
      }
      console.log("Removing event listener");
      this.$refs.messageChat.$el.removeEventListener(
        "scroll",
        this.handleScroll
      );
    },

    onMessageInput(input) {
      let message = {
        body: input.body,
        user: auth.currentUser.uid,
        userDisplay: auth.currentUser.displayName,
        isVectric: this.isAdmin,
        timestamp: firebase.firestore.FieldValue.serverTimestamp()
      };
      if (this.replyingTo !== null) {
        message.replyingTo = {
          messageSummary: truncate(this.replyingTo.body, 40),
          userDisplay: this.replyingTo.userDisplay,
          id: this.replyingTo.id
        };
      }
      liveSessionChatCollection
        .doc(this.id)
        .collection("messages")
        .add(message)
        .then(() => this.scrollToBottom())
        .then(() => {
          this.replyingTo = null;
        })
        .catch(error => console.error("Error adding a document", error));
    }
  },
  computed: {
    ...mapGetters(["loggedIn", "isAdmin"]),
    showScroller() {
      return !this.scrollAtBottom;
    }
  }
};
</script>

<style scoped>
.smooth-scroll {
  scroll-behavior: smooth;
}

.scroller {
  height: 100%;
}

.wrapper-chat {
  height: 400px;
}
</style>
